import React, { useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export default function VideoWindow({
  coverLayerAUrl,
  coverLayerBUrl,
  coverLayerAType,
  coverLayerBType,
  coverLayerAOpacity = 1,
  coverLayerBOpacity = 0,
  coverLayerANoFadeIn = false,
  coverLayerBNoFadeIn = false,
  showCover,
  visualFadeMs = 1000,
  shouldShowVideoWindow,
  videoDockStyle,
  videoWindowStyle,
  dockingReady,
  isVideoFullscreen,
  setIsVideoFullscreen,
  shouldShowVideoSubtitles,
  videoRef,
  miniLyricTextRef,
  miniLyricTransTextRef,
  autoHidden,
  setAutoHidden,
  showPlaylist,
  setShowPlaylist,
  setShowVideoWindow
}) {
  const hasVideoLayer = coverLayerAType === 'video' || coverLayerBType === 'video';
  const shouldRenderCover = showCover && (coverLayerAType === 'cover' || coverLayerBType === 'cover');
  const shouldRenderVisual = hasVideoLayer || shouldRenderCover;
  const videoLayer = coverLayerAType === 'video' ? 'A' : (coverLayerBType === 'video' ? 'B' : null);

  // 手势识别相关状态
  const touchStartRef = useRef(null);
  const mouseStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const containerRef = useRef(null);
  const MIN_SWIPE_DISTANCE = 30; // 最小滑动距离（px）
  const [dragVisual, setDragVisual] = useState({ active: false, deltaY: 0 });
  const [mouseDragActive, setMouseDragActive] = useState(false);

  // 处理触摸开始
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
    }
  }, []);

  // 处理触摸移动（原生事件，非 passive）
  const handleTouchMoveNative = useCallback((e) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直滑动
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
      e.preventDefault(); // 防止页面滚动（使用原生事件监听器，可以调用 preventDefault）
      setDragVisual({
        active: true,
        deltaY: Math.max(Math.min(deltaY, 120), -120)
      });
    }
  }, []);

  // React 事件处理（用于兼容性，但不调用 preventDefault）
  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直滑动
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
      setDragVisual({
        active: true,
        deltaY: Math.max(Math.min(deltaY, 120), -120)
      });
    }
  }, []);

  // 处理触摸结束
  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) {
      touchStartRef.current = null;
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // 判断是否为有效的滑动手势（即使没有标记为拖拽，也检查距离）
    if (distance > MIN_SWIPE_DISTANCE && deltaTime < 500 && Math.abs(deltaY) > Math.abs(deltaX)) {
      // 向上滑动
      if (deltaY < -MIN_SWIPE_DISTANCE) {
        // 只在事件可取消时才调用 preventDefault
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        if (autoHidden) {
          // 播放器隐藏，显示完整播放器
          if (setAutoHidden) setAutoHidden(false);
        } else {
          // 播放器正常显示，显示歌曲列表
          if (setShowPlaylist) setShowPlaylist(true);
        }
      }
      // 向下滑动
      else if (deltaY > MIN_SWIPE_DISTANCE) {
        // 只在事件可取消时才调用 preventDefault
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        // 隐藏封面窗口和视频窗口
        if (setShowVideoWindow) setShowVideoWindow(false);
      }
    }

    touchStartRef.current = null;
    isDraggingRef.current = false;
    setDragVisual({ active: false, deltaY: 0 });
  }, [autoHidden, setAutoHidden, setShowPlaylist, setShowVideoWindow]);

  // 处理鼠标按下
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) { // 左键
      mouseStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
      setMouseDragActive(true);
    }
  }, []);

  // 处理鼠标移动
  const handleMouseMove = useCallback((e) => {
    if (!mouseStartRef.current) return;
    const deltaX = e.clientX - mouseStartRef.current.x;
    const deltaY = e.clientY - mouseStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直拖拽
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
      setDragVisual({
        active: true,
        deltaY: Math.max(Math.min(deltaY, 120), -120)
      });
    }
  }, []);

  // 处理鼠标抬起
  const handleMouseUp = useCallback((e) => {
    if (!mouseStartRef.current || !isDraggingRef.current) {
      mouseStartRef.current = null;
      setMouseDragActive(false);
      return;
    }

    const deltaX = e.clientX - mouseStartRef.current.x;
    const deltaY = e.clientY - mouseStartRef.current.y;
    const deltaTime = Date.now() - mouseStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // 判断是否为有效的拖拽手势
    if (distance > MIN_SWIPE_DISTANCE && deltaTime < 500) {
      // 向上拖拽
      if (deltaY < -MIN_SWIPE_DISTANCE && Math.abs(deltaY) > Math.abs(deltaX)) {
        if (autoHidden) {
          // 播放器隐藏，显示完整播放器
          if (setAutoHidden) setAutoHidden(false);
        } else {
          // 播放器正常显示，显示歌曲列表
          if (setShowPlaylist) setShowPlaylist(true);
        }
      }
      // 向下拖拽
      else if (deltaY > MIN_SWIPE_DISTANCE && Math.abs(deltaY) > Math.abs(deltaX)) {
        // 隐藏封面窗口和视频窗口
        if (setShowVideoWindow) setShowVideoWindow(false);
      }
    }

    mouseStartRef.current = null;
    isDraggingRef.current = false;
    setDragVisual({ active: false, deltaY: 0 });
    setMouseDragActive(false);
  }, [autoHidden, setAutoHidden, setShowPlaylist, setShowVideoWindow]);

  // 处理鼠标离开（防止鼠标移出窗口时状态未重置）
  const handleMouseLeave = useCallback(() => {
    mouseStartRef.current = null;
    isDraggingRef.current = false;
    setDragVisual({ active: false, deltaY: 0 });
    setMouseDragActive(false);
  }, []);

  // 处理触摸开始（原生事件，非 passive）
  const handleTouchStartNative = useCallback((e) => {
    if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
    }
  }, []);

  // 使用原生事件监听器来处理触摸事件（非 passive，可以调用 preventDefault）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStartNative, { passive: false });
    container.addEventListener('touchmove', handleTouchMoveNative, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStartNative);
      container.removeEventListener('touchmove', handleTouchMoveNative);
    };
  }, [handleTouchStartNative, handleTouchMoveNative]);

  useEffect(() => {
    if (!mouseDragActive) return;
    const handleMove = (e) => handleMouseMove(e);
    const handleUp = (e) => handleMouseUp(e);
    const handleBlur = () => handleMouseLeave();

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [handleMouseLeave, handleMouseMove, handleMouseUp, mouseDragActive]);

  if (typeof document === 'undefined') return null;
  if (!shouldRenderVisual) return null;

  // 目标：播放时让“视频浮窗底边”与“播放器顶边”严格对齐。
  // 注意：播放器容器自身已经包含了 safe-area padding（env(safe-area-inset-bottom)），
  // 所以这里只能使用播放器写入的高度变量，避免 double count 导致错位。
  const gapPx = 4; // 与 useVideoDocking 的 gap 保持一致：始终留缝
  const showOffsetY = shouldShowVideoWindow ? 0 : 40;

  return createPortal(
    <div
      ref={containerRef}
      className=""
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof setIsVideoFullscreen === 'function') {
          setIsVideoFullscreen((prev) => !prev);
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      style={{
        // 重要：强制 fixed，避免 Tailwind 未加载或被覆盖时失效
        position: 'fixed',
        opacity: shouldShowVideoWindow ? 1 : 0,
        // 保持可见以便隐藏动画正常播放
        visibility: 'visible',
        pointerEvents: shouldShowVideoWindow && (isVideoFullscreen || dockingReady) ? 'auto' : 'none',
        boxSizing: 'border-box',
        // 防止在封面/视频窗口上滑动时触发页面滚动
        touchAction: 'none',
        overscrollBehavior: 'contain',
        transform: `translate3d(0, ${dragVisual.deltaY + showOffsetY}px, 0)`,
        transition: dragVisual.active
          ? 'transform 16ms linear'
          : 'transform 440ms ease-out, opacity 440ms ease-out',
        // 将间距变量写入样式，确保始终保留 4px 缝隙
        '--tenryu-video-gap': `${gapPx}px`,
        // 优先使用 videoDockStyle（包含完整的定位和尺寸信息）
        // 如果 videoDockStyle 为空，使用 videoWindowStyle 或默认尺寸，并添加定位
        ...(videoDockStyle || {
          ...(videoWindowStyle?.width ? videoWindowStyle : { width: '640px', height: '360px' }),
          // 添加默认定位：手机贴左；桌面居中（播放器上方）
          left: videoWindowStyle?.left || ((typeof window !== 'undefined' && window.innerWidth < 768) ? '0px' : '50%'),
          top: 'auto',
          // 关键：底部永远贴齐“展开后的播放器顶部”
          // 通过播放器自身写入的 CSS 变量 --tenryu-player-h 实现（最稳，不依赖 DOM 计算顺序）
          bottom: `calc(var(--tenryu-player-h, 0px) + var(--tenryu-video-gap, ${gapPx}px))`,
          transform: videoWindowStyle?.left
            ? 'none'
            : ((typeof window !== 'undefined' && window.innerWidth < 768) ? 'none' : 'translateX(-50%)'),
          zIndex: 9998
        }),
        // 关键：当 useVideoDocking 提供了 videoDockStyle（top 基于 player rect.top 计算）时，
        // 必须信任它的 top 定位，否则 bottom 方案会因为高度变量/安全区时序导致“看起来没贴齐”。
        // 只有在没有 videoDockStyle 的兜底情况下，才使用 bottom + CSS 变量的方式。
        ...(videoDockStyle
          ? null
          : {
              top: 'auto',
              bottom: `calc(var(--tenryu-player-h, 0px) + var(--tenryu-video-gap, ${gapPx}px))`
            }),
        // 立体样式外框（视频全屏时也保留，并稍微增强可见性）
        ...(isVideoFullscreen
          ? {
              border: '4px solid rgba(255, 255, 255, 0.5)',
              borderRadius: '10px',
              boxShadow: `
                0 0 0 1px rgba(0, 0, 0, 0.55),
                0 0 0 2px rgba(255, 255, 255, 0.08),
                inset 0 1px 0 rgba(255, 255, 255, 0.14),
                inset 0 -1px 0 rgba(0, 0, 0, 0.35),
                0 10px 28px rgba(0, 0, 0, 0.55),
                0 22px 60px rgba(0, 0, 0, 0.45)
              `,
              padding: '4px',
              background: 'rgba(0, 0, 0, 0.35)'
            }
          : {
              border: '3px solid rgba(255, 255, 255, 0.5)',
              borderRadius: '8px',
              boxShadow: `
                0 0 0 1px rgba(0, 0, 0, 0.5),
                0 0 0 2px rgba(255, 255, 255, 0.05),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                inset 0 -1px 0 rgba(0, 0, 0, 0.3),
                0 4px 12px rgba(0, 0, 0, 0.4),
                0 8px 24px rgba(0, 0, 0, 0.3)
              `,
              padding: '3px',
              background: 'rgba(0, 0, 0, 0.3)'
            })
      }}
    >
      <div
        className="w-full h-full bg-black overflow-hidden relative"
        style={{
          borderRadius: '5px',
          backgroundImage: "url('/images/PlayBackground.webp')",
          backgroundPosition: 'bottom center',
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          height: '100%'
        }}
      >
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            width: '100%',
            height: '100%',
            opacity: coverLayerAOpacity,
            transition: `opacity ${(coverLayerANoFadeIn ? 0 : (visualFadeMs || 0))}ms ease-in-out`
          }}
        >
          {coverLayerAType === 'video' && videoLayer === 'A' && (
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              preload="metadata"
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
          )}
          {coverLayerAType === 'cover' && showCover && (
            <img
              src={coverLayerAUrl || ''}
              alt=""
              className="w-full h-full object-cover"
              style={{
                width: '100%',
                height: '100%',
                display: coverLayerAUrl ? 'block' : 'none',
                objectFit: 'contain',
                objectPosition: 'center'
              }}
            />
          )}
        </div>
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            width: '100%',
            height: '100%',
            opacity: coverLayerBOpacity,
            transition: `opacity ${(coverLayerBNoFadeIn ? 0 : (visualFadeMs || 0))}ms ease-in-out`
          }}
        >
          {coverLayerBType === 'video' && videoLayer === 'B' && (
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              preload="metadata"
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
          )}
          {coverLayerBType === 'cover' && showCover && (
            <img
              src={coverLayerBUrl || ''}
              alt=""
              className="w-full h-full object-cover"
              style={{
                width: '100%',
                height: '100%',
                display: coverLayerBUrl ? 'block' : 'none',
                objectFit: 'contain',
                objectPosition: 'center'
              }}
            />
          )}
        </div>
        {/* 字幕叠加显示在视频表面上（视频模式不受 showLyrics 影响） */}
        {shouldShowVideoSubtitles && (
          <div
            className="absolute bottom-0 left-0 right-0 px-4 py-3 select-none"
            style={{
              background: 'linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.3) 50%, transparent 100%)',
              pointerEvents: 'none'
            }}
          >
            <div className="text-center">
              <div
                ref={miniLyricTextRef}
                className="font-extrabold tracking-wide transition-all duration-300 text-white"
                style={{
                  color: '#ffffff'
                  // 描边/阴影由 updateMiniLyric 统一设置（外描边 text-shadow）
                }}
              >
                {/* text set by JS */}
              </div>
              <div
                ref={miniLyricTransTextRef}
                className="mt-1 font-bold tracking-wide text-white"
                style={{
                  display: 'none',
                  color: '#ffffff'
                  // 描边/阴影由 updateMiniLyric 统一设置（外描边 text-shadow）
                }}
              >
                {/* translation set by JS */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

